import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS, Step } from 'react-joyride'
import { TourGuideContext, TourGuideProps } from './context'

import Path from 'router'
import React from 'react'
import { ReactNode } from 'react'
import { Tooltip } from './components/tooltip'
import { toast } from 'react-toastify'
import { tourGuideSteps } from './services/guide'
import useFirstVisit from 'context/tour-guide/services/use-tourguide'
import { useNavigate } from 'react-router-dom'
import { useWindowSize } from 'hooks'

interface Props {
  children: ReactNode | ReactNode[] | null
}

export const SCROLL_TIME = 300
export const checkPointTargets = ['#tour-anchor']

export const TourGuideProvider: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate()
  const { firstVisitComplete } = useFirstVisit()
  const { isMobile, isTablet } = useWindowSize()
  const [steps, setSteps] = React.useState<Step[]>([])
  const [run, setRun] = React.useState(false)
  const [requestTourStart, setRequestTourStart] = React.useState(false)
  const [currentStep, setCurrentStep] = React.useState(0)

  // flag, set based on whether a product has been clicked
  const [backFromPortfolio, setBackFromPortfolio] = React.useState(false)
  const [overviewSectionComplete, setOverviewSectionComplete] = React.useState(false)
  const [productSectionComplete, setProductSectionComplete] = React.useState(false)
  const [mobileBuySellComplete, setMobileBuySellComplete] = React.useState<
    'IDLE' | 'GOBACK' | 'CLICKED' | 'COMPLETE'
  >('IDLE' as const)
  const [menuOpen, setMenuOpen] = React.useState<'REQUESTOPEN' | 'OPEN' | 'CLOSED'>(
    'CLOSED' as const,
  )
  const [performanceSectionOpen, setPerformanceSectionOpen] = React.useState<'OPEN' | 'CLOSED'>(
    'CLOSED' as const,
  )
  const [portfolioState, setPortfolioState] = React.useState<'IDLE' | 'CLICKED' | 'OPEN'>(
    'IDLE' as const,
  )
  const [mobilePortfolio, setMobilePortfolio] = React.useState<
    'IDLE' | 'GOBACK' | 'CLICKED' | 'COMPLETE'
  >('IDLE' as const)
  const [hasCompletedTour, setHasCompletedTour] = React.useState(false)
  const globalLayout = (document as Document).getElementById('app-layout')

  /**
   * IT: sets the correct steps
   * WHEN: the window size changes
   */
  React.useEffect(() => {
    const guide = tourGuideSteps(isMobile || isTablet)
    if (run) {
      stopTour()
      setSteps(guide)
      startTour()
    } else {
      setSteps(guide)
    }
  }, [isMobile, isTablet])

  // todo add logic to check local storage
  const startTour = () => {
    setRequestTourStart(false)
    setRun(true)
  }
  const stopTour = () => {
    setRun(false)
  }

  // sets request flag and move to the first step
  const requestStartTour = () => {
    setRequestTourStart(true)
    navigate(Path.MAIN)
  }

  const data: TourGuideProps = {
    startTour,
    stopTour,
    currentStep,
    setCurrentStep,
    hasCompletedTour,
    requestTourStart,
    run,
    requestStartTour,
    // Tour stages Stage 1 - Overview
    setOverviewSectionComplete,
    overviewSectionComplete,
    productSectionComplete,
    setProductSectionComplete,
    mobileBuySellComplete,
    setMobileBuySellComplete,
    portfolioState,
    setPortfolioState,
    mobilePortfolio,
    setMobilePortfolio,
    menuOpen,
    setMenuOpen,
    performanceSectionOpen,
    setPerformanceSectionOpen,
    backFromPortfolio,
    setBackFromPortfolio,
  }

  /**
   * Updates the current step index, interested components can wrap current step in a useEffect to trigger events around the tour
   * @param param0
   * @returns
   */
  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index, status, type, step } = data
    const nextStep = index + (action === ACTIONS.PREV ? -1 : 1)
    const readyToMoveForward = () => {
      return action === ACTIONS.START && status === STATUS.RUNNING
    }

    /**
     * === Handle Styles for checkpoint targets, remove the overlay fade if we hit a checkpoint target selector ===
     */
    if (
      checkPointTargets.includes(step?.target as string) &&
      (status === STATUS.RUNNING || status === STATUS.IDLE)
    ) {
      const overlay = document.querySelector('.react-joyride__overlay') as HTMLElement
      if (overlay) overlay.style.backgroundColor = 'transparent'
    }

    //Handle closing out the tour early (user skipped) or when the tour has finished
    if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
      localStorage.setItem('tourCompleted', 'true')
      stopTour()
      setHasCompletedTour(true)
      firstVisitComplete()
      setCurrentStep(0)
      if (status === STATUS.FINISHED) {
        navigate(Path.MAIN)
      }
      return
    } else if (([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as string[]).includes(type)) {
      /**
       * Handle stepping "out of" the menu
       */
      if (
        index === 12 &&
        menuOpen === 'OPEN' &&
        (action === ACTIONS.NEXT || action === ACTIONS.PREV)
      ) {
        if (action === ACTIONS.PREV) {
          setCurrentStep(nextStep - 2)
          setTimeout(() => {
            setMenuOpen('CLOSED')
          }, 350)
        }

        if (action === ACTIONS.NEXT) {
          setProductSectionComplete(true)
          toast.dismiss()
        }
      }

      // get next step
      setCurrentStep(nextStep)

      /*
       * === Tour Stages GO BACK LOGIC ===
       */

      // Stage 1 - Overview (go back)
      if (currentStep === 2 && nextStep === 1 && action === ACTIONS.PREV) {
        setRun(false)
        setCurrentStep(nextStep)
        navigate(Path.MAIN)
      }

      // Ther performance charts
      if (
        (nextStep === 5 && action === ACTIONS.NEXT) ||
        (nextStep === 6 && action === ACTIONS.PREV)
      ) {
        setPerformanceSectionOpen('OPEN')
        stopTour()
        setTimeout(() => {
          setCurrentStep(nextStep)
          startTour()
        }, 500)
        return
      }

      if (
        (nextStep === 4 && action === ACTIONS.PREV) ||
        (nextStep === 7 && action === ACTIONS.NEXT)
      ) {
        setCurrentStep(nextStep)
        setPerformanceSectionOpen('CLOSED')
        return
      }

      // Stage 3 - Portfolio (go back)
      if (currentStep === 13 && nextStep === 12 && action === ACTIONS.PREV) {
        stopTour()
        setCurrentStep(nextStep - 1)
        setProductSectionComplete(false)
        navigate('/products/cai')
        setBackFromPortfolio(true)
      }

      // Stage 1 - Overview (go back)
      if (currentStep === 11 && nextStep === 10 && action === ACTIONS.PREV) {
        toast.dismiss()
        setCurrentStep(nextStep)
      }
      if (currentStep === 8 && action === ACTIONS.PREV) {
        setMobileBuySellComplete('GOBACK')
        setCurrentStep(nextStep)
      }

      // must return here, otherwise the next step will be set twice
      return
    }

    //Scrolling to the top for Home-Product-Portfolio tooltips
    if (index === 0 || index === 2 || index === 13) {
      globalLayout?.scrollTo({ top: 0, behavior: 'smooth' })
    }

    /**
     * === Tour Stages GO FORWARD LOGIC ===
     */

    //Stage 1 - Overview (go forward)
    if (readyToMoveForward() && currentStep === 1 && overviewSectionComplete) {
      setCurrentStep(nextStep)
    }
  }

  return (
    <TourGuideContext.Provider value={data}>
      <Joyride
        callback={(data) => handleJoyrideCallback(data)}
        run={run}
        steps={steps}
        continuous={true}
        tooltipComponent={Tooltip}
        stepIndex={currentStep}
        disableScrolling={false}
        disableScrollParentFix={true}
        scrollOffset={120}
        scrollDuration={SCROLL_TIME}
        disableCloseOnEsc={true}
        styles={{
          options: {
            overlayColor: 'rgb(255, 255, 255, 0.5)',
          },
        }}
      />
      {children}
    </TourGuideContext.Provider>
  )
}
